import { useState, useCallback, useEffect, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import { trTR, enUS } from "@mui/x-data-grid";
import { trTR as coretrTR, enUS as coreenUS } from "@mui/material/locale";
import { useHttpClient } from "shared/hooks/http-hook";
import { useTranslation } from "react-i18next";
import { PERMISSION_TYPES } from "@routebot/constants";
import { getDeviceId } from "shared/util/auth/device-id";

// import { redirect } from "react-router-dom";

let logoutTimer;

// Impersonation is a purely client-side overlay stored in localStorage.
// These helpers keep the overlay in sync with the active session so the
// banner can never linger after the impersonation session is gone.
const clearImpersonationData = () => {
  localStorage.removeItem("isImpersonating");
  localStorage.removeItem("adminClientData");
  localStorage.removeItem("impersonatedClient");
};

// A page refresh during a network blip used to look exactly like a dead token: the client fetch
// failed once and the user landed on /login although the session in localStorage was fine.
// Only a 4xx answer means the session is gone; anything else (no connection, 5xx) is retried.
const CLIENT_FETCH_RETRY_DELAYS_MS = [1000, 2500, 5000];
const isSessionRejected = (err) => Number.isInteger(err?.code) && err.code >= 400 && err.code < 500;

const fetchClientWithRetry = async (sendRequest, clientId, token) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients/${clientId}?populate=1`, "GET", null, {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      });
    } catch (err) {
      if (isSessionRejected(err) || attempt >= CLIENT_FETCH_RETRY_DELAYS_MS.length) throw err;
      await new Promise((resolve) => setTimeout(resolve, CLIENT_FETCH_RETRY_DELAYS_MS[attempt]));
    }
  }
};

const readSession = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    if (parsed && parsed.token && new Date(parsed.expiration) > new Date()) {
      return parsed;
    }
  } catch {}
  return null;
};

export const useAuth = () => {
  // login related
  const { i18n } = useTranslation();

  const [token, setToken] = useState();
  const [tokenExpirationDate, setTokenExpirationDate] = useState();
  const [id, setId] = useState(false);

  // client related
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [schools, setSchools] = useState();
  const [company, setCompany] = useState();
  const [timezones, setTimezones] = useState([]);
  const [timezone, setTimezone] = useState("Europe/Istanbul");
  const [permissions, setPermissions] = useState();
  const [countryCode, setCountryCode] = useState("TR");
  const [brand, setBrand] = useState("servistesin");
  const [client, setClient] = useState();

  // theme related
  const [theme, setTheme] = useState();

  // state related
  const [loaded, setLoaded] = useState(false);

  const { sendRequest } = useHttpClient();

  const resetUser = () => {
    setToken(null);
    setTokenExpirationDate(null);
    setId(null);

    setName(null);
    setEmail(null);
    setSchools(null);
    setCompany(null);
    setTimezones(null);
    setTimezone(null);
    setLoaded(false);
    localStorage.removeItem("clientData");
    // Never leave orphan impersonation flags behind on logout.
    clearImpersonationData();
  };

  const getClientInfo = useCallback(
    async (clientId, token) => {
      if (clientId) {
        try {
          const responseData = await fetchClientWithRetry(sendRequest, clientId, token);

          if (typeof window !== "undefined" && typeof window.clarity === "function") {
            try {
              window.clarity("identify", responseData.client.name.replace(" ", ""));
            } catch {}
          }

          setName(responseData.client.name);
          setSchools(responseData.client?.schools);
          setCompany(responseData.client?.company);
          setBrand(responseData.client?.company?.brand || "servistesin");
          setEmail(responseData.client?.email);
          setTimezones(responseData?.client?.timezones || []);
          if (responseData.client?.timezone) {
            setTimezone(responseData.client?.timezone);
          } else {
            setTimezone("Europe/Istanbul");
          }
          setCountryCode(responseData.client?.countryCode || "TR");
          setClient(responseData.client);
          const permissions = {};
          Object.values(PERMISSION_TYPES).forEach((key) => {
            permissions[key] = responseData.client?.permissions?.[key] || false;
          });

          setPermissions(permissions);

          return true;
        } catch (err) {}
      }
      return false;
    },
    [sendRequest]
  );

  const login = useCallback(
    async (clientId, token, expirationDate = null) => {
      try {
        const clientInfoLoaded = await getClientInfo(clientId, token);
        if (!clientInfoLoaded) {
          setLoaded(true);
          return false;
        }

        // store token in local storage
        const tokenExpirationDate = expirationDate || new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365);

        // login related
        setToken(token);
        setId(clientId);
        setTokenExpirationDate(tokenExpirationDate);

        localStorage.setItem(
          "clientData",
          JSON.stringify({
            clientId,
            token,
            expiration: tokenExpirationDate.toISOString(),
          })
        );

        setLoaded(true);
        return true;
      } catch (err) {
        console.log(err);
      }

      setLoaded(true);
      return false;
    },
    [getClientInfo]
  );

  const logout = useCallback(async () => {
    const deviceId = getDeviceId();

    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/logout`,
        "DELETE",
        JSON.stringify({
          deviceId: deviceId,
        })
      );
    } catch (err) {
      console.log(err);
    }

    resetUser();
    window.location.href = "/login";
    // redirect("/login");
  }, []);

  const UpdateTheme = useCallback((theme = "en") => {
    setTheme(
      createTheme(
        {
          // palette: {
          //   primary: { main: "#1976d2" },
          //   secondary: { main: "#dc004e" },
          // },
          typography: {
            fontFamily: "'Open Sans', sans-serif",
          },
        },
        // Dil desteği eklendiğinde burayı değiştir
        theme === "tr" ? trTR : enUS,
        theme === "tr" ? coretrTR : coreenUS,
        //Simdilik TR
        //trTR, //Burayı değiştir
        //coreenUS, //Burayı değiştir
        {
          GridCsvExportOptions: { fileName: "gh" },
        }
      )
    );
  }, []);

  // auto logout
  useEffect(() => {
    if (token && tokenExpirationDate) {
      const remainingTime = tokenExpirationDate.getTime() - new Date().getTime();
      logoutTimer = setTimeout(logout, remainingTime);
    } else {
      clearTimeout(logoutTimer);
    }
  }, [token, logout, tokenExpirationDate]);

  // auto login
  useEffect(() => {
    let storedLang = localStorage.getItem("i18nextLng");

    if (!storedLang || storedLang === "undefined") {
      storedLang = i18n.resolvedLanguage || "en";
      localStorage.setItem("i18nextLng", storedLang);
      i18n.changeLanguage(storedLang);
    }
    UpdateTheme(storedLang);

    const bootstrap = async () => {
      const activeSession = readSession("clientData");
      const isImpersonating = localStorage.getItem("isImpersonating") === "true";
      const adminSession = isImpersonating ? readSession("adminClientData") : null;

      const restoreAdmin = async () => {
        // Impersonation session is gone but we still have a valid admin
        // session saved: drop the stale banner and fall back to admin.
        clearImpersonationData();
        localStorage.setItem("clientData", JSON.stringify(adminSession));
        await login(adminSession.clientId, adminSession.token, new Date(adminSession.expiration));
      };

      if (activeSession) {
        const ok = await login(activeSession.clientId, activeSession.token, new Date(activeSession.expiration));
        // Active token is dead (e.g. expired/revoked impersonation token).
        if (!ok && isImpersonating && adminSession) {
          await restoreAdmin();
        }
      } else if (isImpersonating && adminSession) {
        // Impersonation token already expired; recover the admin session.
        await restoreAdmin();
      } else {
        // No usable session: make sure no orphan impersonation banner remains.
        if (isImpersonating) clearImpersonationData();
        setLoaded(true);
      }
    };

    bootstrap();
  }, [UpdateTheme, i18n, login]);

  const authContextValue = useMemo(
    () => ({
      id,
      token,
      tokenExpirationDate,
      name,
      email,
      permissions,
      schools,
      company,
      brand,
      timezones,
      timezone,
      theme,
      loaded,
      countryCode,
      client,
      login,
      logout,
      UpdateTheme,
    }),
    [
      id,
      token,
      tokenExpirationDate,
      name,
      email,
      permissions,
      schools,
      company,
      brand,
      countryCode,
      timezone,
      timezones,
      theme,
      loaded,
      client,
      login,
      logout,
      UpdateTheme,
    ]
  );

  return authContextValue;
};
